import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  Package,
  Calendar,
  AlertTriangle,
  X,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { useGearRequests } from '@/hooks/use-gear-requests';
import { useProject, useProjects } from '@/hooks/use-projects';
import { useOrgTimeZone, useOrgToday } from '@/hooks/use-settings';
import { addLocalDays, formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { GearRequest, GearRequestStatus, GearRequestPriority } from '@/lib/types';

const statusColors: Record<GearRequestStatus, string> = {
  pending_confirmation: 'bg-amber-100 text-amber-800',
  requested: 'bg-yellow-100 text-yellow-800',
  fulfilled: 'bg-blue-100 text-blue-800',
  in_transit: 'bg-purple-100 text-purple-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  returned: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const statusLabels: Record<GearRequestStatus, string> = {
  pending_confirmation: 'Pending Confirmation',
  requested: 'Requested',
  fulfilled: 'Fulfilled',
  in_transit: 'In Transit',
  partially_received: 'Partially Received',
  received: 'Received',
  returned: 'Returned to Inventory',
  cancelled: 'Cancelled',
};

const priorityColors: Record<GearRequestPriority, string> = {
  low: 'bg-gray-100 text-gray-700',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const PAGE_SIZE = 20;

function isOverdue(neededBy: string | null, orgToday: string): boolean {
  if (!neededBy) return false;
  return neededBy.slice(0, 10) < orgToday;
}

export function GearRequestsPage() {
  const orgTimeZone = useOrgTimeZone();
  const orgToday = useOrgToday();
  const [searchParams, setSearchParams] = useSearchParams();
  const showOverdueOnly = searchParams.get('overdue') === '1';

  const page = parseInt(searchParams.get('page') || '1', 10);
  const statusFilter = (searchParams.get('status') || '') as GearRequestStatus | '';
  const projectFilter = searchParams.get('project') || '';
  const neededByBefore = searchParams.get('needed_by_before') || '';

  const { data, isLoading, isError, refetch } = useGearRequests({
    page,
    page_size: PAGE_SIZE,
    status: statusFilter,
    project: projectFilter ? parseInt(projectFilter, 10) : '',
    needed_by_before: showOverdueOnly ? addLocalDays(orgToday, -1) : neededByBefore,
  });

  const { data: projectsData } = useProjects({ page_size: 100 });
  const { data: selectedProject } = useProject(
    projectFilter ? parseInt(projectFilter, 10) : 0
  );

  const requests = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const projects = projectsData?.results || [];

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleOverdueToggle = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    if (showOverdueOnly) {
      newParams.delete('overdue');
    } else {
      newParams.set('overdue', '1');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  }, [showOverdueOnly, searchParams, setSearchParams]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Gear Requests
          </h1>
          <p className="text-gray-600 mt-1">
            Fulfillment queue for project gear requests
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select
              value={statusFilter}
              onValueChange={(value) =>
                updateSearchParams({ status: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_confirmation">Pending Confirmation</SelectItem>
                <SelectItem value="requested">Requested</SelectItem>
                <SelectItem value="fulfilled">Fulfilled</SelectItem>
                <SelectItem value="in_transit">In Transit</SelectItem>
                <SelectItem value="partially_received">Partially Received</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={projectFilter}
              onValueChange={(value) =>
                updateSearchParams({ project: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="w-full sm:w-[220px]">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <label htmlFor="needed-by-filter" className="text-sm text-gray-600 whitespace-nowrap">
                Needed by:
              </label>
              <Input
                id="needed-by-filter"
                type="date"
                value={neededByBefore}
                onChange={(e) =>
                  updateSearchParams({ needed_by_before: e.target.value })
                }
                className="w-full sm:w-[180px]"
                disabled={showOverdueOnly}
              />
            </div>

            <Button
              variant={showOverdueOnly ? 'default' : 'outline'}
              onClick={handleOverdueToggle}
              className="whitespace-nowrap"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Overdue Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {projectFilter && (
        <Badge variant="secondary" className="w-fit gap-1 py-1 pl-3 pr-1">
          Project: {selectedProject?.name || `#${projectFilter}`}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 rounded-full"
            onClick={() => updateSearchParams({ project: '' })}
            aria-label="Remove project filter"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </Badge>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load gear requests. Please try again.
            </div>
          ) : requests.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No gear requests found.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Requester</TableHead>
                  <TableHead>Lines</TableHead>
                  <TableHead>Needed By</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((req) => (
                  <GearRequestRow
                    key={req.id}
                    request={req}
                    timeZone={orgTimeZone}
                    orgToday={orgToday}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() =>
                  updateSearchParams({ page: String(Math.max(1, page - 1)) })
                }
                className={
                  page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                }
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() =>
                      updateSearchParams({ page: String(pageNum) })
                    }
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  updateSearchParams({
                    page: String(Math.min(totalPages, page + 1)),
                  })
                }
                className={
                  page >= totalPages
                    ? 'pointer-events-none opacity-50'
                    : 'cursor-pointer'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}

interface GearRequestRowProps {
  request: GearRequest;
  timeZone: string;
  orgToday: string;
}

function GearRequestRow({ request, timeZone, orgToday }: GearRequestRowProps) {
  const overdue =
    isOverdue(request.needed_by, orgToday) &&
    request.status !== 'received' &&
    request.status !== 'cancelled';

  return (
    <TableRow>
      <TableCell>
        <Link
          to={`/gear-requests/${request.id}`}
          className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
          #{request.id}
        </Link>
      </TableCell>
      <TableCell>
        <Badge className={statusColors[request.status]}>
          {statusLabels[request.status]}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge className={priorityColors[request.priority]}>
          {request.priority}
        </Badge>
      </TableCell>
      <TableCell>{request.requested_by_username || '—'}</TableCell>
      <TableCell>{request.lines.length}</TableCell>
      <TableCell>
        {request.needed_by ? (
          <div
            className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}
          >
            <Calendar className="h-4 w-4" />
            <span>{formatDateOnly(request.needed_by)}</span>
            {overdue && (
              <Badge variant="destructive" className="ml-1">
                Overdue
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </TableCell>
      <TableCell className="text-gray-500">
        {formatDateTimeInTimeZone(request.created_at, timeZone)}
      </TableCell>
    </TableRow>
  );
}
